import React, { useEffect, useRef, useState } from 'react';
import { canLoadTapNative, getSafeTapNativeUrl } from '../utils/tapNativePrivacy';
import {
  captureTapNativeAdCreatives,
  registerTapNativeIdentityCapture,
} from '../utils/tapNativeIdentityCapture';

function hasRenderedAd(container) {
  if (!container) return false;

  const renderedElement = Array.from(
    container.querySelectorAll('a[href], iframe, img, [role="link"]')
  ).some((element) => {
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return (
      rect.width > 2 &&
      rect.height > 2 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0'
    );
  });
  const renderedText = container.textContent?.trim();
  const containerRect = container.getBoundingClientRect();

  return Boolean(renderedElement || (renderedText?.length > 20 && containerRect.height > 20));
}

export default function TapNativeAdUnit({
  adUnitId,
  placement = 'content',
  isAuthenticated = false,
  isHipaaMode = false,
  label = 'Sponsored',
  fallbackLabel = 'Related resources',
  fallback = null,
  fallbackDelayMs = 6000,
  className = '',
  minHeightClass = 'min-h-[80px]',
  captureIdentity = false,
  variant = 'standard',
}) {
  const containerId = `adx_native_ad_${adUnitId}`;
  const containerRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const isCompact = variant === 'compact';

  useEffect(() => {
    if (!adUnitId || typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const container = containerRef.current;
    if (!container) return undefined;

    let cancelled = false;
    let creativeSnapshotTimer = null;
    container.innerHTML = '';

    // TapNative injects third-party JavaScript in the page context. Keep it off
    // non-public routes, sensitive query strings, authenticated sessions, and
    // HIPAA/PHI-capable app surfaces; URL sanitization alone is not isolation.
    if (
      !canLoadTapNative({
        pathname: window.location.pathname,
        search: window.location.search,
        isAuthenticated,
        isHipaaMode,
      })
    ) {
      setStatus('blocked');
      return undefined;
    }

    setStatus('loading');

    if (captureIdentity) {
      registerTapNativeIdentityCapture({ adUnitId, placement });
    }

    const captureCreativeSnapshot = (renderStatus) => {
      if (!captureIdentity || cancelled) return;
      captureTapNativeAdCreatives({
        adUnitId,
        placement,
        container,
        status: renderStatus,
      });
    };

    const scheduleCreativeSnapshot = (renderStatus) => {
      if (!captureIdentity || cancelled) return;
      if (creativeSnapshotTimer) {
        window.clearTimeout(creativeSnapshotTimer);
      }
      creativeSnapshotTimer = window.setTimeout(() => {
        creativeSnapshotTimer = null;
        captureCreativeSnapshot(renderStatus);
      }, 800);
    };

    const markFilledIfReady = () => {
      window.requestAnimationFrame(() => {
        if (!cancelled && hasRenderedAd(container)) {
          setStatus('filled');
          scheduleCreativeSnapshot('filled');
        }
      });
    };

    const observer = new MutationObserver(markFilledIfReady);
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const previousScript = document.querySelector(
      `script[data-tapnative-ad-unit="${adUnitId}"]`
    );
    if (previousScript) previousScript.remove();

    const params = new URLSearchParams();
    params.set('aid', String(adUnitId));
    params.set('r', String(Math.random() * 1e16));
    params.set('url', getSafeTapNativeUrl());

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://content.tapnative.com/tn/?${params.toString()}`;
    script.dataset.tapnativeAdUnit = String(adUnitId);
    script.addEventListener('load', markFilledIfReady);
    script.addEventListener('error', () => {
      if (!cancelled) {
        captureCreativeSnapshot('error');
        setStatus('empty');
      }
    });

    const fallbackTimer = window.setTimeout(() => {
      if (!hasRenderedAd(container)) {
        captureCreativeSnapshot('empty');
        setStatus('empty');
      }
    }, fallbackDelayMs);

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.body.appendChild(script);
    }

    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
      if (creativeSnapshotTimer) {
        window.clearTimeout(creativeSnapshotTimer);
      }
      observer.disconnect();
      script.remove();
    };
  }, [adUnitId, captureIdentity, fallbackDelayMs, isAuthenticated, isHipaaMode, placement]);

  const showFallback = status === 'empty' && fallback;
  const showAdSlot = status === 'filled';

  if (status === 'blocked') {
    return null;
  }

  if (status === 'empty' && !fallback) {
    return null;
  }

  return (
    <aside
      className={
        showAdSlot || showFallback
          ? isCompact
            ? `relative my-0 max-h-[20px] overflow-hidden border-0 p-0 ${className}`
            : `relative my-6 border-y border-[#e5e5ea] py-3 ${className}`
          : 'relative my-0 h-0 overflow-hidden border-0 p-0'
      }
      aria-label="Sponsored content"
      data-ad-status={status}
      data-ad-placement={placement}
      data-ad-variant={variant}
    >
      {showAdSlot && label && (
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">
          {label}
        </div>
      )}
      {showFallback && fallbackLabel && (
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">
          {fallbackLabel}
        </div>
      )}
      <div
        ref={containerRef}
        id={containerId}
        className={showAdSlot ? minHeightClass : 'h-0 min-h-0 overflow-hidden'}
      />
      {showFallback && fallback}
    </aside>
  );
}
